/**
 * Balanced spatial partition for the automated territory creator (M8, R2). A
 * deterministic, re-runnable heuristic: k-means for spatial coherence, then a
 * capacity-balancing pass that moves boundary points from over-weight to
 * under-weight clusters until the weights are roughly equal. "Balance" is by
 * point count (weight 1) or by a numeric column's value.
 *
 * Per the milestone's R2 mitigation this is explicitly "good enough" — balanced
 * and re-runnable, hand-editable via lasso afterward; contiguity is not promised
 * in v1. Pure and dependency-free, so it is unit-testable and worker-portable.
 */

import type { Point } from "@/lib/map/pointset";

export interface PartitionOptions {
  /** Target territory count. */
  count: number;
  /** Numeric column to balance by; when absent, balance by point count. */
  balanceBy?: string;
  /** k-means iterations (bounded for the main-thread budget). */
  iterations?: number;
  /** Rebalance moves cap (bounded so a pathological input can't spin). */
  maxRebalanceMoves?: number;
}

export interface PartitionResult {
  /** Cluster index (0..count-1) per input point, aligned to `points`. */
  assignment: number[];
  /** Total weight per cluster (point count, or summed numeric column). */
  weights: number[];
}

function weightOf(p: Point, balanceBy: string | undefined): number {
  if (!balanceBy) return 1;
  const n = Number(p.props[balanceBy]);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Squared planar distance in lng/lat space — cheap, monotone (no sqrt needed). */
function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/**
 * Deterministic seeding: sort points by (lng, lat) and pick `k` evenly spaced
 * along that order. No RNG, so a re-run over the same input reproduces the same
 * partition (the "re-runnable" promise).
 */
function seedCentroids(points: Point[], k: number): [number, number][] {
  const order = points
    .map((p, i) => ({ i, x: p.lng, y: p.lat }))
    .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  const seeds: [number, number][] = [];
  for (let c = 0; c < k; c++) {
    const idx = Math.min(order.length - 1, Math.floor(((c + 0.5) / k) * order.length));
    const p = points[order[idx]!.i]!;
    seeds.push([p.lng, p.lat]);
  }
  return seeds;
}

/**
 * Partition `points` into `count` roughly balanced clusters. Returns the
 * per-point cluster assignment plus each cluster's total weight.
 */
export function balancedPartition(points: Point[], opts: PartitionOptions): PartitionResult {
  const k = Math.max(1, Math.min(opts.count, points.length));
  const iterations = opts.iterations ?? 12;
  const balanceBy = opts.balanceBy;
  const n = points.length;

  if (n === 0) return { assignment: [], weights: Array.from({ length: opts.count }, () => 0) };
  if (k === 1) {
    let w = 0;
    for (const p of points) w += weightOf(p, balanceBy);
    return { assignment: points.map(() => 0), weights: [w] };
  }

  const weights = points.map((p) => weightOf(p, balanceBy));
  let centroids = seedCentroids(points, k);
  let assignment = new Array<number>(n).fill(0);

  // --- k-means for spatial coherence -------------------------------------
  for (let iter = 0; iter < iterations; iter++) {
    let moved = false;
    for (let i = 0; i < n; i++) {
      const p = points[i]!;
      let best = 0;
      let bestD = Infinity;
      for (let c = 0; c < k; c++) {
        const d = dist2(p.lng, p.lat, centroids[c]![0], centroids[c]![1]);
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      }
      if (assignment[i] !== best) {
        assignment[i] = best;
        moved = true;
      }
    }
    const sumX = new Array<number>(k).fill(0);
    const sumY = new Array<number>(k).fill(0);
    const cnt = new Array<number>(k).fill(0);
    for (let i = 0; i < n; i++) {
      const c = assignment[i]!;
      sumX[c]! += points[i]!.lng;
      sumY[c]! += points[i]!.lat;
      cnt[c]! += 1;
    }
    centroids = centroids.map((cur, c) =>
      cnt[c]! > 0 ? [sumX[c]! / cnt[c]!, sumY[c]! / cnt[c]!] : cur,
    );
    if (!moved) break;
  }

  // --- capacity rebalance -------------------------------------------------
  const clusterWeight = new Array<number>(k).fill(0);
  for (let i = 0; i < n; i++) clusterWeight[assignment[i]!]! += weights[i]!;
  const maxMoves = opts.maxRebalanceMoves ?? n * 4;
  // Smallest movable unit — once the spread is within one unit, no move can
  // improve balance (moving a unit would just flip over/under), so we stop.
  let minUnit = Infinity;
  for (const w of weights) if (w > 0 && w < minUnit) minUnit = w;
  if (!Number.isFinite(minUnit)) minUnit = 1;

  for (let move = 0; move < maxMoves; move++) {
    // Heaviest and lightest clusters.
    let over = 0;
    let under = 0;
    for (let c = 1; c < k; c++) {
      if (clusterWeight[c]! > clusterWeight[over]!) over = c;
      if (clusterWeight[c]! < clusterWeight[under]!) under = c;
    }
    if (over === under) break;
    const spread = clusterWeight[over]! - clusterWeight[under]!;
    if (spread <= minUnit) break;

    // Move the over-cluster point nearest the under-cluster's centroid whose
    // weight does not overshoot balance on arrival (spread must be >= 2w, else
    // the swap flips over/under to a worse gap and would oscillate).
    let cx = 0;
    let cy = 0;
    let ucnt = 0;
    for (let i = 0; i < n; i++) {
      if (assignment[i] === under) {
        cx += points[i]!.lng;
        cy += points[i]!.lat;
        ucnt++;
      }
    }
    if (ucnt > 0) {
      cx /= ucnt;
      cy /= ucnt;
    }
    let pick = -1;
    let pickD = Infinity;
    for (let i = 0; i < n; i++) {
      if (assignment[i] !== over) continue;
      const w = weights[i]!;
      if (w <= 0 || spread < 2 * w) continue;
      const d = dist2(points[i]!.lng, points[i]!.lat, cx, cy);
      if (d < pickD) {
        pickD = d;
        pick = i;
      }
    }
    if (pick < 0) break;
    const w = weights[pick]!;
    assignment[pick] = under;
    clusterWeight[over]! -= w;
    clusterWeight[under]! += w;
  }

  return { assignment, weights: clusterWeight };
}
